import { useEffect, useId, useRef } from 'react';

export default function DefinitionPopover({
  term,
  definition,
}: {
  term: string;
  definition: string;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLSpanElement>(null);
  const supported = typeof HTMLElement !== 'undefined' && 'showPopover' in HTMLElement.prototype;

  useEffect(() => {
    const button = trigger.current;
    const popup = panel.current;
    if (!supported || !button || !popup) return;
    let timer: ReturnType<typeof setTimeout>;
    let hovering = false;
    let pinned = false;
    let dismissed = false;
    const isOpen = () => popup.matches(':popover-open');
    const position = () => {
      if (!isOpen()) return;
      const rect = button.getBoundingClientRect();
      const width = popup.offsetWidth;
      const height = popup.offsetHeight;
      popup.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
      popup.style.top = `${Math.max(8, Math.min(rect.bottom + 4 + height <= window.innerHeight - 8 ? rect.bottom + 4 : rect.top - height - 4, window.innerHeight - height - 8))}px`;
    };
    const show = () => {
      clearTimeout(timer);
      if (dismissed) return;
      if (!isOpen()) popup.showPopover();
      position();
    };
    const hide = () => {
      clearTimeout(timer);
      pinned = false;
      if (isOpen()) popup.hidePopover();
    };
    const enter = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      hovering = true;
      clearTimeout(timer);
      timer = setTimeout(show, 200);
    };
    const leave = () => {
      hovering = false;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!hovering && document.activeElement !== button && !pinned) {
          hide();
          dismissed = false;
        }
      }, 150);
    };
    const blur = () => {
      pinned = false;
      if (!hovering) {
        hide();
        dismissed = false;
      }
    };
    const click = () => {
      if (pinned) {
        dismissed = true;
        hide();
      } else {
        dismissed = false;
        show();
        pinned = true;
      }
    };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen()) {
        event.preventDefault();
        event.stopPropagation();
        dismissed = true;
        hide();
      }
    };
    const toggle = () => {
      if (!isOpen()) {
        pinned = false;
        dismissed = hovering || document.activeElement === button;
      }
    };
    button.addEventListener('focus', show);
    button.addEventListener('blur', blur);
    button.addEventListener('click', click);
    popup.addEventListener('toggle', toggle);
    for (const element of [button, popup]) {
      element.addEventListener('pointerenter', enter);
      element.addEventListener('pointerleave', leave);
    }
    document.addEventListener('keydown', keydown, true);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      clearTimeout(timer);
      button.removeEventListener('focus', show);
      button.removeEventListener('blur', blur);
      button.removeEventListener('click', click);
      popup.removeEventListener('toggle', toggle);
      for (const element of [button, popup]) {
        element.removeEventListener('pointerenter', enter);
        element.removeEventListener('pointerleave', leave);
      }
      document.removeEventListener('keydown', keydown, true);
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [supported]);

  if (!supported)
    return (
      <>
        {term} ({definition})
      </>
    );
  return (
    <>
      <button ref={trigger} type="button" className="definition-term" aria-describedby={id}>
        {term}
      </button>
      <span
        ref={panel}
        id={id}
        {...{ popover: 'auto' }}
        role="tooltip"
        className="definition-panel"
      >
        {definition}
      </span>
      <span className="definition-print"> ({definition})</span>
    </>
  );
}
